import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cx from 'clsx';
import dayjs from 'dayjs';

import { IconRcTick } from '@/assets';
import { IconRcStreamDrawHot, IconRcStreamXp, IconRcStreamPrizeCash } from '@/assets/stream';
import { RestrictionTip, Tips, OrgVerifiedLogo, ProgressBar } from '@/components';
import { MobileModalTip } from '@/components/shared/MobileModalTip';
import { useDeviceTypeIsMobile, useTheme, useSwitch, OfficialAccountVerifyStatus } from '@/hooks';
import { FeedArticle } from '@/views/Stream';
import { formatNum, formatNetworth, isOfficalAddress } from '@/utils';

import { openJoinerModal } from './JoinerModal';
import { ActionList } from './ActionList';
import { ExtraXP } from './ExtraXP';
import style from './index.module.scss';
import { RefererXP } from './RefererXP';

export const QuestCard = ({
  article,
  isDetails,
  maxDisplay,
}: {
  article: FeedArticle;
  isDetails?: boolean;
  maxDisplay?: number;
}) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const { isDark } = useTheme();
  const { on: mobileTip, turnOn: showMobileTip, turn: setMobileTip } = useSwitch();

  const quest = article.quest!;
  const draw = article.quest!.draw;

  const isOfficialCreator = useMemo(
    () => isOfficalAddress(article.creator_id),
    [article.creator_id],
  );

  const endsTime = useMemo(
    () => {
      const time = dayjs.unix(article.create_at + quest!.duration);

      if (time.isToday()) {
        return `${t('DrawCard.today')} ${time.format('HH:mm')}`;
      }

      if (time.isTomorrow()) {
        return `${t('DrawCard.tomorrow')} ${time.format('HH:mm')}`;
      }

      return time.format('MM/DD HH:mm');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article.create_at, quest!.duration],
  );

  const isClaimAllXP = useMemo(
    () => quest?._claimedXp === quest?.unit_xp,
    [quest?._claimedXp, quest?.unit_xp],
  );

  const joinCountTip = useMemo(
    () => (
      <div className={style.joinCountTip}>
        <div className={style.item}>
          <span>{t('QuestCard.estimatedJoinCount')}</span>
          <span>{quest._estimatedJoinCount}</span>
        </div>
        <div className={style.item}>
          <span>{t('QuestCard.joinCount')}</span>
          <span>{quest._joinCount}</span>
        </div>
      </div>
    ),
    [quest._estimatedJoinCount, quest._joinCount, t],
  );

  const handleUserListClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();

    if (isMobile) {
      showMobileTip();
      return;
    }

    const { type } = e.currentTarget.dataset;
    const isWinner = type === 'winner';

    openJoinerModal({
      isWinner,
      id: isWinner ? quest.draw?.id! : quest.id,
    });
  };

  const isClosed = quest.status === 'closed';

  // 还没参加 draw
  const unJoinDraw = !draw?.is_joined && !isClosed;

  return (
    <>
      <div className={cx(style.container, isDark && style.isDark, isMobile && style.isMobile)}>
        <div className={style.header}>
          <div className={cx(style.creator, isOfficialCreator && style.isOfficial)}>
            <Trans i18nKey="QuestCard.questBy" values={{ name: article._author }}>
              Quest by
              {isOfficialCreator ? (
                <OrgVerifiedLogo
                  size={16}
                  marginLeft={4}
                  marginRight={4}
                  disableTooltip
                  unverified={
                    (article?.creator as OfficialAccount)?.verify_status !==
                    OfficialAccountVerifyStatus.VERIFIED
                  }
                />
              ) : null}
              {article._author}
            </Trans>
          </div>
          <div className={cx(style.status, quest.status !== 'active' && style.isClosed)}>
            {quest.status === 'active' ? (
              quest.is_completed ? (
                t('QuestCard.16t7ona')
              ) : quest.is_joined ? (
                t('QuestCard.kah1o8')
              ) : (
                t('QuestCard.active')
              )
            ) : (
              <>
                {t('QuestCard.xffrt8')}
                {quest.is_completed ? (
                  <span className={style.selfStatus}>({t('QuestCard.16t7ona')})</span>
                ) : quest.is_joined ? (
                  <span className={style.selfStatus}>({t('QuestCard.kah1o8')})</span>
                ) : null}
              </>
            )}
          </div>
        </div>
        <div className={cx(style.main, !quest.unit_xp && !draw && style.noReward)}>
          <div className={style.title}>{quest.name}</div>
          <div className={cx(style.rewards, draw && style.withDraw)}>
            {quest.unit_xp ? (
              <div className={style.reward}>
                <div className={style.rewardTitle}>
                  <IconRcStreamXp className={style.rewardIcon} /> XP
                </div>
                <div className={cx(style.rewardValue, isClaimAllXP && style.done)}>
                  {isClaimAllXP ? <IconRcTick className={style.icon} /> : null}
                  {quest._xp}
                </div>
                <div className={style.progressBarContainer}>
                  <ProgressBar
                    className={style.progressBar}
                    max={quest?.unit_xp}
                    progress={quest._claimedXp}
                  />
                  <div className={style.progress}>{`${quest._claimedXp} / ${quest?.unit_xp}`}</div>
                </div>
              </div>
            ) : null}
            {draw ? (
              <div className={style.reward}>
                <div className={cx(style.rewardTitle, unJoinDraw && style.unJoinDraw)}>
                  <IconRcStreamPrizeCash className={style.rewardIcon} />
                  {t('DrawCard.cash-prize')}
                </div>
                <div className={cx(style.rewardValue, unJoinDraw && style.unJoinDraw)}>
                  {draw.prize_value ? formatNetworth(draw.prize_value / 10000, true) : '$0.00'}
                  <div className={style.prizeCount}>× {draw._prize_count}</div>
                </div>
                {draw?.is_joined ? (
                  <div
                    className={cx(
                      style.drawStatus,
                      draw?.is_settled && draw?.is_winner && style.winner,
                    )}>
                    {draw?.is_settled
                      ? draw?.is_winner
                        ? t('DrawCard.1jq1ooh')
                        : t('DrawCard.32cijd')
                      : isClosed
                        ? t('DrawCard.1hic812')
                        : t('QuestCard.1lk78io')}
                  </div>
                ) : unJoinDraw ? (
                  <div className={style.unJoinDrawTxt}>{t('QuestCard.zw5612')}</div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className={style.state}>
            <div className={style.joinState}>
              {draw?.is_joined && draw?.is_winner ? (
                <div className={style.userState}>{t('DrawCard.cenapq')}</div>
              ) : null}
              {quest.status === 'active' && !quest?.has_permission ? (
                <div className={style.userState}>{t('QuestCard_Task.noeligible')}</div>
              ) : null}
              <div className={style.endTime}>{t('DrawCard.yos3f', { time: endsTime })}</div>
            </div>
          </div>
        </div>
        <div className={style.footer}>
          <RestrictionTip data={quest.permissions} />
          <div className={style.footerRight}>
            <IconRcStreamDrawHot className={style.hotIcon} />
            <div
              className={cx(style.userCount, quest.join_count && style.isClickable)}
              onClick={quest.join_count ? handleUserListClick : undefined}>
              {`${quest._joinCount}/${quest._estimatedJoinCount}`} {t('QuestCard.participantNum')}
            </div>
            <Tips title={joinCountTip} />
            {draw?.winner_count ? (
              <>
                <div className={style.seperator} />
                <span
                  className={cx(style.userCount, style.isClickable)}
                  data-type="winner"
                  onClick={handleUserListClick}>
                  {formatNum(draw.winner_count)}{' '}
                  {t('DrawCard.1dupss4', { count: draw.winner_count })}
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      {isMobile && mobileTip ? (
        <MobileModalTip visible={mobileTip} setVisible={setMobileTip} />
      ) : null}
      {isDetails ? <ActionList article={article} /> : null}
      {isDetails ? <ExtraXP article={article} /> : null}
      {isDetails ? <RefererXP articleId={article?.id} /> : null}
    </>
  );
};
